import type { StockTransfer } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Dialog,
  FormField,
  Input,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PackageCheck, Pencil, Send, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import {
  formatStockQuantity,
  receiveLinesBody,
  receiveShortage,
  transferActions,
  transferValueBalance,
} from "../../../../features/market/market-values.js";
import { useMarketPermissions, useRefreshMarket, useTransfer } from "../../../../features/market/queries.js";
import {
  crumbLink,
  Field,
  LedgerStatus,
  MarketPage,
  TableFrame,
  TransferStatusBadge,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { TransferDialog } from "../../../../features/market/transfer-dialog.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/market/transferler/$id")({ component: TransferDetailRoute });

function TransferDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const transfer = useTransfer(id);
  const title = transfer.data?.transferNo ?? t("market.transfers.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketTransfers"), "/market/transferler"), { label: title }]}
    >
      {() =>
        transfer.error ? (
          <Alert tone="danger">{errorMessage(transfer.error)}</Alert>
        ) : !transfer.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <TransferDetail transfer={transfer.data} />
        )
      }
    </MarketPage>
  );
}

function TransferDetail({ transfer }: { transfer: StockTransfer }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const stations = useMarketStations(transfer.companyId);
  const [editing, setEditing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const actions = transferActions(transfer.status);
  const balance = transferValueBalance(transfer);
  const canManage = permissions.canManageStock;

  const act = useMutation({
    mutationFn: async (action: "send" | "cancel") => {
      const params = { path: { id: transfer.id } };
      if (action === "send")
        return (
          await api.POST("/market/transfers/{id}/send", { params, body: { version: transfer.version } })
        ).data!;
      return (
        await api.POST("/market/transfers/{id}/cancel", {
          params,
          body: { version: transfer.version, reason },
        })
      ).data!;
    },
    onSuccess: async (_data, action) => {
      setCancelling(false);
      setReason("");
      await refresh();
      toast({ tone: "success", title: t(`market.transfers.done.${action}`) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{transfer.transferNo}</span>
        <TransferStatusBadge status={transfer.status} />
        <span className="text-sm text-muted-foreground">
          {transfer.fromStationName} <span aria-hidden="true">→</span>
          <span className="sr-only">{t("market.transfers.toSr")}</span> {transfer.toStationName}
        </span>
        {canManage ? (
          <div className="ms-auto flex flex-wrap gap-2">
            {actions.edit ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil aria-hidden="true" />
                {t("common.edit")}
              </Button>
            ) : null}
            {actions.send ? (
              <Button loading={act.isPending && act.variables === "send"} onClick={() => act.mutate("send")}>
                <Send aria-hidden="true" />
                {t("market.transfers.send")}
              </Button>
            ) : null}
            {actions.receive ? (
              <Button onClick={() => setReceiving(true)}>
                <PackageCheck aria-hidden="true" />
                {t("market.transfers.receive")}
              </Button>
            ) : null}
            {actions.cancel ? (
              <Button variant="destructive" onClick={() => setCancelling(true)}>
                <X aria-hidden="true" />
                {t("market.transfers.cancel")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {transfer.status === "SENT" ? <Alert tone="info">{t("market.transfers.inTransitHint")}</Alert> : null}

      <Section title={t("market.transfers.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.transfers.from")}>
            {transfer.fromStationName} · <span className="font-mono">{transfer.fromWarehouseCode}</span>
          </Field>
          <Field label={t("market.transfers.to")}>
            {transfer.toStationName} · <span className="font-mono">{transfer.toWarehouseCode}</span>
          </Field>
          <Field label={t("market.transfers.fields.sentAt")}>{formatDateTime(transfer.sentAt)}</Field>
          <Field label={t("market.transfers.fields.receivedAt")}>{formatDateTime(transfer.receivedAt)}</Field>
          <Field label={t("market.fields.ledger")}>
            <LedgerStatus status={transfer.ledgerStatus} journalEntryId={transfer.journalEntryId} />
          </Field>
          <Field label={t("market.fields.createdAt")}>{formatDateTime(transfer.createdAt)}</Field>
        </dl>
        {transfer.note ? <p className="text-sm">{transfer.note}</p> : null}
      </Section>

      {transfer.status === "SENT" || transfer.status === "RECEIVED" ? (
        <Section title={t("market.transfers.valueTitle")}>
          <dl className="grid gap-3 sm:grid-cols-3" data-testid="transfer-value-summary">
            <Field label={t("market.transfers.fields.sentValue")}>
              <MoneyText value={balance.sent} />
            </Field>
            <Field label={t("market.transfers.fields.receivedValue")}>
              <MoneyText value={balance.received} />
            </Field>
            <Field label={t("market.transfers.fields.shortValue")}>
              <MoneyText value={balance.short} />
            </Field>
          </dl>
          {transfer.status === "RECEIVED" ? (
            balance.balanced ? (
              <p className="text-sm text-muted-foreground">{t("market.transfers.valueBalanced")}</p>
            ) : (
              <Alert tone="warning">{t("market.transfers.valueUnbalanced")}</Alert>
            )
          ) : null}
        </Section>
      ) : null}

      <Section title={t("market.transfers.lines")}>
        <TableFrame label={t("market.transfers.lines")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("market.fields.product")}</Th>
              <Th>{t("market.fields.lotNo")}</Th>
              <Th align="right">{t("market.transfers.fields.sentQuantity")}</Th>
              <Th align="right">{t("market.transfers.fields.receivedQuantity")}</Th>
              <Th align="right">{t("market.transfers.fields.shortQuantity")}</Th>
              <Th align="right">{t("market.transfers.fields.sentValue")}</Th>
              <Th align="right">{t("market.transfers.fields.shortValue")}</Th>
            </tr>
          </thead>
          <tbody>
            {transfer.lines.map((line) => (
              <tr key={line.id} className="border-t">
                <td className="px-3 py-2">
                  <span className="grid gap-0.5">
                    <span>{line.productName}</span>
                    <span className="font-mono text-xs text-muted-foreground">{line.productCode}</span>
                    {line.shortNote ? (
                      <span className="text-xs text-muted-foreground">{line.shortNote}</span>
                    ) : null}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {line.lotId === null
                    ? t("market.lines.fefoShort")
                    : (line.lotNo ?? t("market.lots.noLotNo"))}
                </td>
                <td className="px-3 py-2 text-right tabular">{formatStockQuantity(line.sentQuantity)}</td>
                <td className="px-3 py-2 text-right tabular">{formatStockQuantity(line.receivedQuantity)}</td>
                <td className="px-3 py-2 text-right">
                  {line.shortQuantity !== null && !Decimal.from(line.shortQuantity).isZero() ? (
                    <StatusBadge tone="warning">{formatStockQuantity(line.shortQuantity)}</StatusBadge>
                  ) : (
                    <span className="tabular">{formatStockQuantity(line.shortQuantity)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={line.sentValue} />
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={line.shortValue} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </Section>

      {canManage && actions.edit ? (
        <TransferDialog
          open={editing}
          onOpenChange={setEditing}
          companyId={transfer.companyId}
          stations={stations.stations}
          defaultFromStationId={transfer.fromStationId}
          transfer={transfer}
          onSaved={() => undefined}
        />
      ) : null}
      {canManage && actions.receive ? (
        <ReceiveDialog open={receiving} onOpenChange={setReceiving} transfer={transfer} />
      ) : null}

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("market.transfers.cancelTitle")}
        description={t("market.transfers.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("market.fields.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={300}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              loading={act.isPending && act.variables === "cancel"}
              disabled={reason.trim().length < 3}
            >
              {t("market.transfers.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

/**
 * Teslim alma: satır başına gelen miktar (boş = tam geldi). Gönderilenden az girilen miktar EKSİK olarak yazılır
 * ve olağandışı kayıp (fire) fişine gider — kullanıcı göndermeden önce bunu açıkça görür.
 */
function ReceiveDialog({
  open,
  onOpenChange,
  transfer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transfer: StockTransfer;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const [received, setReceived] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const shortages = transfer.lines.map((line) => ({
    line,
    short: receiveShortage(line.sentQuantity, received[line.id] ?? ""),
  }));
  const invalid = shortages.some((item) => item.short === null);
  const shortLines = shortages.filter((item) => item.short !== null && item.short.isPositive());

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/market/transfers/{id}/receive", {
          params: { path: { id: transfer.id } },
          body: {
            version: transfer.version,
            lines: receiveLinesBody(transfer.lines, received, notes),
          } as never,
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("market.transfers.done.receive") });
      onOpenChange(false);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("market.transfers.receiveTitle")}
      description={t("market.transfers.receiveDescription")}
      className="sm:max-w-2xl"
    >
      <form
        className="grid max-h-[70vh] gap-4 overflow-y-auto pr-1"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          if (!invalid) save.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {transfer.lines.map((line, index) => {
          const short = shortages[index]!.short;
          return (
            <fieldset key={line.id} className="grid gap-2 rounded-md border p-3">
              <legend className="px-1 text-sm font-semibold">
                {line.productCode} {line.productName}
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  label={t("market.transfers.receivedFor", { product: line.productName })}
                  description={t("market.transfers.sentHint", {
                    quantity: formatStockQuantity(line.sentQuantity),
                  })}
                  error={short === null ? t("market.transfers.receivedInvalid") : undefined}
                >
                  {(control) => (
                    <Input
                      {...control}
                      inputMode="decimal"
                      className="text-right tabular"
                      placeholder={formatStockQuantity(line.sentQuantity)}
                      value={received[line.id] ?? ""}
                      onChange={(event) => setReceived((all) => ({ ...all, [line.id]: event.target.value }))}
                    />
                  )}
                </FormField>
                {short !== null && short.isPositive() ? (
                  <FormField label={t("market.transfers.shortNoteFor", { product: line.productName })}>
                    {(control) => (
                      <Input
                        {...control}
                        maxLength={300}
                        value={notes[line.id] ?? ""}
                        onChange={(event) => setNotes((all) => ({ ...all, [line.id]: event.target.value }))}
                      />
                    )}
                  </FormField>
                ) : null}
              </div>
            </fieldset>
          );
        })}
        {shortLines.length > 0 ? (
          <Alert tone="warning" title={t("market.transfers.shortTitle")}>
            {t("market.transfers.shortBody", { count: shortLines.length })}
          </Alert>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={save.isPending} disabled={invalid}>
            <PackageCheck aria-hidden="true" />
            {t("market.transfers.confirmReceive")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
